/**
 * Location Management Page - Centers table with search, filter, and CRUD actions
 */

import { useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import styled from 'styled-components';
import { Plus, Search, Pencil, Power, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Badge } from '../../components/ui/Badge';
import { ResponsiveTableWrapper } from '../../components/ui';
import locationService, { Location, DeactivatePreCheck } from '../../services/api/locationService';
import { useAuthStore } from '../../stores/authStore';
import { CreateLocationModal } from './CreateLocationModal';
import { EditLocationModal } from './EditLocationModal';
import { DeactivateDialog } from './DeactivateDialog';
import { BulkLocationUploadModal } from './BulkLocationUploadModal';
import { LocationDetailDrawer } from '../LocationDetailPage/LocationDetailDrawer';

// ==================== Styled Components ====================

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[6]};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing[4]};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.typography.fontSize['3xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const HeaderActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[3]};
  align-items: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    flex-direction: column;
    width: 100%;
  }
`;

const SearchContainer = styled.div`
  flex: 1;
  max-width: 320px;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    max-width: 100%;
  }
`;

const FilterContainer = styled.div`
  min-width: 140px;
`;

const TableWrapper = styled.div`
  background: ${({ theme }) => theme.colors.backgrounds.white};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 840px;
`;

const Thead = styled.thead`
  background: ${({ theme }) => theme.colors.backgrounds.gray};
`;

const Th = styled.th`
  text-align: left;
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[4]}`};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borders.light};
  white-space: nowrap;
`;

const ThActions = styled(Th)`
  width: 90px;
`;

const Tbody = styled.tbody``;

const Tr = styled.tr`
  cursor: pointer;

  &:not(:last-child) td {
    border-bottom: 1px solid ${({ theme }) => theme.colors.borders.light};
  }

  &:hover {
    background: ${({ theme }) => theme.colors.backgrounds.page};
  }
`;

const Td = styled.td`
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[4]}`};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  vertical-align: middle;
`;

const LocationNameLink = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.primary.ocean};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  font-size: inherit;
  padding: 0;
  text-align: left;

  &:hover {
    text-decoration: underline;
  }
`;

const LocationSubtext = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.light};
  margin-top: ${({ theme }) => theme.spacing[1]};
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[1]};
  align-items: center;
`;

const IconButton = styled.button<{ $variant?: 'default' | 'danger' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: none;
  cursor: pointer;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  color: ${({ theme, $variant }) =>
    $variant === 'danger' ? theme.colors.status.error.text : theme.colors.text.secondary};
  transition: ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme, $variant }) =>
      $variant === 'danger' ? theme.colors.status.error.bg : theme.colors.backgrounds.gray};
    color: ${({ theme, $variant }) =>
      $variant === 'danger' ? theme.colors.status.error.text : theme.colors.text.primary};
  }

  svg {
    width: 15px;
    height: 15px;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing[12]} ${({ theme }) => theme.spacing[4]};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const SkeletonBar = styled.div<{ $width?: string }>`
  height: 14px;
  width: ${({ $width }) => $width ?? '100%'};
  background: ${({ theme }) => theme.colors.backgrounds.gray};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  animation: pulse 1.5s ease-in-out infinite;

  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
`;

const Pagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[4]}`};
  border-top: 1px solid ${({ theme }) => theme.colors.borders.light};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[2]};
  }
`;

const PaginationInfo = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const PaginationControls = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[2]};
  align-items: center;
`;

const PageButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid ${({ theme }) => theme.colors.borders.light};
  background: ${({ theme }) => theme.colors.backgrounds.white};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  cursor: pointer;
  color: ${({ theme }) => theme.colors.text.secondary};
  transition: ${({ theme }) => theme.transitions.fast};

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.backgrounds.gray};
    color: ${({ theme }) => theme.colors.text.primary};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

// ==================== Status options ====================

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

// ==================== Component ====================

export const LocationManagementPage = () => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [locationToEdit, setLocationToEdit] = useState<Location | null>(null);
  const [deactivateTarget, setDeactivateTarget] = useState<Location | null>(null);
  const [preCheck, setPreCheck] = useState<DeactivatePreCheck | null>(null);
  const [showDeactivateDialog, setShowDeactivateDialog] = useState(false);

  const navigate = useNavigate();
  const { id: selectedLocationId } = useParams<{ id?: string }>();
  const queryClient = useQueryClient();
  const { hasPermission } = useAuthStore();

  // Stable reference — prevents drawer's Escape-key effect from re-subscribing on every render.
  const handleDrawerClose = useCallback(() => navigate('/locations'), [navigate]);

  const statusParam =
    statusFilter === 'active' ? true : statusFilter === 'inactive' ? false : undefined;

  const { data, isLoading } = useQuery({
    queryKey: ['locations', page, search, statusFilter],
    queryFn: () =>
      locationService.getLocations({
        page,
        limit: 20,
        search: search || undefined,
        status: statusParam,
      } as any),
  });

  const deactivateMutation = useMutation({
    mutationFn: (id: string) => locationService.deactivateLocation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['locations'] });
      closeDeactivateDialog();
    },
  });

  const handleDeactivateClick = async (e: React.MouseEvent, location: Location) => {
    e.stopPropagation();
    const check = await locationService.getDeactivatePreflight(location.id);
    setPreCheck(check);
    setDeactivateTarget(location);
    setShowDeactivateDialog(true);
  };

  const closeDeactivateDialog = () => {
    setShowDeactivateDialog(false);
    setDeactivateTarget(null);
    setPreCheck(null);
    deactivateMutation.reset();
  };

  const handleEditClick = (e: React.MouseEvent, location: Location) => {
    e.stopPropagation();
    setLocationToEdit(location);
  };

  const locations = data?.data ?? [];
  const meta = data?.meta;
  const totalPages = meta?.totalPages ?? 1;
  const from = meta ? (meta.page - 1) * meta.limit + 1 : 0;
  const to = meta ? Math.min(meta.page * meta.limit, meta.total) : 0;

  const canEdit = hasPermission('locations:manage');
  const canDelete = hasPermission('locations:manage');
  const canManage = canEdit || canDelete;

  return (
    <Container>
      <Header>
        <Title>Centers</Title>
        <HeaderActions>
          <SearchContainer>
            <Input
              type="search"
              placeholder="Search by name or city..."
              leftIcon={<Search />}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              fullWidth
            />
          </SearchContainer>
          <FilterContainer>
            <Select
              options={STATUS_OPTIONS}
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              fullWidth
            />
          </FilterContainer>
          {hasPermission('locations:manage') && (
            <>
              <Button
                variant="secondary"
                leftIcon={<Plus />}
                onClick={() => setShowBulkUpload(true)}
              >
                Bulk Upload
              </Button>
              <Button
                variant="primary-blue"
                leftIcon={<Plus />}
                onClick={() => setShowCreateModal(true)}
              >
                Create Center
              </Button>
            </>
          )}
        </HeaderActions>
      </Header>

      <TableWrapper>
        <ResponsiveTableWrapper>
          <Table>
            <Thead>
              <tr>
                <Th>Name</Th>
                <Th>City / State</Th>
                <Th>Status</Th>
                <Th>Clinicians</Th>
                <Th>Clients</Th>
                {canManage && <ThActions />}
              </tr>
            </Thead>
            <Tbody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i}>
                    <Td><SkeletonBar $width="180px" /></Td>
                    <Td><SkeletonBar $width="120px" /></Td>
                    <Td><SkeletonBar $width="60px" /></Td>
                    <Td><SkeletonBar $width="40px" /></Td>
                    <Td><SkeletonBar $width="40px" /></Td>
                    {canManage && <Td />}
                  </tr>
                ))
              ) : locations.length === 0 ? (
                <tr>
                  <td colSpan={canManage ? 6 : 5}>
                    <EmptyState>
                      {search || statusFilter
                        ? 'No centers match your filters.'
                        : 'No centers yet. Create your first center.'}
                    </EmptyState>
                  </td>
                </tr>
              ) : (
                locations.map((loc) => (
                  <Tr key={loc.id} onClick={() => navigate(`/locations/${loc.id}`)}>
                    <Td>
                      <LocationNameLink
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/locations/${loc.id}`);
                        }}
                      >
                        {loc.name}
                      </LocationNameLink>
                      {loc.street && <LocationSubtext>{loc.street}</LocationSubtext>}
                    </Td>
                    <Td>
                      {loc.city}, {loc.state}
                    </Td>
                    <Td>
                      <Badge variant={loc.isActive ? 'success' : 'neutral'}>
                        {loc.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </Td>
                    <Td>{loc.clinicianCount ?? 0}</Td>
                    <Td>{loc.clientCount ?? 0}</Td>
                    {canManage && (
                      <Td>
                        <ActionButtons>
                          {canEdit && (
                            <IconButton
                              title="Edit center"
                              aria-label="Edit center"
                              onClick={(e) => handleEditClick(e, loc)}
                            >
                              <Pencil />
                            </IconButton>
                          )}
                          {canDelete && loc.isActive && (
                            <IconButton
                              $variant="danger"
                              title="Deactivate center"
                              aria-label="Deactivate center"
                              onClick={(e) => handleDeactivateClick(e, loc)}
                            >
                              <Power />
                            </IconButton>
                          )}
                        </ActionButtons>
                      </Td>
                    )}
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
        </ResponsiveTableWrapper>

        {!isLoading && locations.length > 0 && meta && (
          <Pagination>
            <PaginationInfo>
              Showing {from}–{to} of {meta.total} center{meta.total !== 1 ? 's' : ''}
            </PaginationInfo>
            <PaginationControls>
              <PageButton
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                aria-label="Previous page"
              >
                <ChevronLeft />
              </PageButton>
              <PageButton
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                aria-label="Next page"
              >
                <ChevronRight />
              </PageButton>
            </PaginationControls>
          </Pagination>
        )}
      </TableWrapper>

      <CreateLocationModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
      />

      <BulkLocationUploadModal
        isOpen={showBulkUpload}
        onClose={() => setShowBulkUpload(false)}
      />

      <EditLocationModal
        isOpen={!!locationToEdit}
        onClose={() => setLocationToEdit(null)}
        location={locationToEdit}
      />

      <DeactivateDialog
        isOpen={showDeactivateDialog}
        onClose={closeDeactivateDialog}
        onConfirm={() => deactivateTarget && deactivateMutation.mutate(deactivateTarget.id)}
        location={deactivateTarget}
        preCheck={preCheck}
        isLoading={deactivateMutation.isPending}
      />

      {selectedLocationId && (
        <LocationDetailDrawer
          locationId={selectedLocationId}
          onClose={handleDrawerClose}
        />
      )}
    </Container>
  );
};

export default LocationManagementPage;
